import { useCallback, useEffect, useRef, useState } from 'react'
import { useSetAtom } from 'jotai'
import { mergeMessages, replaceOptimistic } from './messageHelpers'
import { subscribeToConversationMessages } from './supportRealtime'
import { supportApi, type SendMessageInput } from './supportApi'
import {
  supportChatOpenAtom,
  supportConversationIdAtom,
  supportUnreadAtom,
} from './supportUnread'
import type { SupportAttachment, SupportConversation, SupportMessage } from './types'

interface UseSupportConversation {
  conversation: SupportConversation | null
  messages: SupportMessage[]
  loading: boolean
  sending: boolean
  error: string | null
  send: (input: SendMessageInput) => Promise<void>
  retry: (clientId: string) => Promise<void>
  reload: () => Promise<void>
}

const newClientId = (): string => `cli-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const optimisticMessage = (
  conversationId: string,
  input: SendMessageInput,
  clientId: string
): SupportMessage => ({
  id: clientId,
  conversationId,
  authorType: 'customer',
  authorId: null,
  body: input.body,
  createdAt: new Date().toISOString(),
  clientId,
  status: 'sending',
  // local previews so the bubble can render the picked images/files before upload completes
  attachments: (input.attachments ?? []).map(
    (a, i): SupportAttachment => ({
      id: `${clientId}-${i}`,
      filename: a.name,
      contentType: a.type,
      size: a.size ?? 0,
      url: a.uri,
    })
  ),
})

export const useSupportConversation = (): UseSupportConversation => {
  const [conversation, setConversation] = useState<SupportConversation | null>(null)
  const [messages, setMessages] = useState<SupportMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const convIdRef = useRef<string | null>(null)
  // remember the last send inputs per clientId so `retry` can re-send
  const pendingInputs = useRef<Map<string, SendMessageInput>>(new Map())

  // DR-2 unread wiring
  const setUnreadConvId = useSetAtom(supportConversationIdAtom)
  const setUnread = useSetAtom(supportUnreadAtom)
  const setChatOpen = useSetAtom(supportChatOpenAtom)

  // Panel mounted ⇒ chat is open; on unmount close (unread watcher will re-poll).
  useEffect(() => {
    setChatOpen(true)
    return () => {
      setChatOpen(false)
    }
  }, [setChatOpen])

  const refetch = useCallback(async (conversationId: string) => {
    const fresh = await supportApi.getMessages(conversationId)
    setMessages((prev) => mergeMessages(prev, fresh))
  }, [])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const conv = await supportApi.getOrCreateConversation()
      setConversation(conv)
      convIdRef.current = conv.id
      // DR-2: remember the conversation for the unread watcher, clear local badge, mark read.
      setUnreadConvId(conv.id)
      setUnread(0)
      void supportApi.markRead(conv.id).catch(() => {})
      await refetch(conv.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load support chat')
    } finally {
      setLoading(false)
    }
  }, [refetch, setUnreadConvId, setUnread])

  useEffect(() => {
    let unsubscribe = (): void => {}
    let cancelled = false
    void (async () => {
      await reload()
      const id = convIdRef.current
      if (!cancelled && id) {
        unsubscribe = subscribeToConversationMessages(id, () => {
          void refetch(id)
          void supportApi.markRead(id).catch(() => {})
        })
      }
    })()
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [reload, refetch])

  const deliver = useCallback(async (clientId: string, input: SendMessageInput) => {
    const id = convIdRef.current
    if (!id) return
    setSending(true)
    setError(null)
    // optimistic: show it immediately as 'sending'
    setMessages((prev) => mergeMessages(prev, [optimisticMessage(id, input, clientId)]))
    try {
      const created = await supportApi.sendMessage(id, { ...input, clientId })
      pendingInputs.current.delete(clientId)
      setMessages((prev) => replaceOptimistic(prev, clientId, created))
    } catch (e) {
      // mark the optimistic bubble failed (keep it for retry) instead of throwing to a banner
      setMessages((prev) =>
        prev.map((m) => (m.clientId === clientId ? { ...m, status: 'failed' } : m))
      )
      setError(e instanceof Error ? e.message : 'Failed to send message')
    } finally {
      setSending(false)
    }
  }, [])

  const send = useCallback(
    async (input: SendMessageInput) => {
      const clientId = newClientId()
      pendingInputs.current.set(clientId, input)
      await deliver(clientId, input)
    },
    [deliver]
  )

  const retry = useCallback(
    async (clientId: string) => {
      const input = pendingInputs.current.get(clientId)
      if (!input) return
      setMessages((prev) =>
        prev.map((m) => (m.clientId === clientId ? { ...m, status: 'sending' } : m))
      )
      await deliver(clientId, input)
    },
    [deliver]
  )

  return { conversation, messages, loading, sending, error, send, retry, reload }
}
